import React, { useState, useEffect } from "react";
import { updateProfile, deleteUser, reauthenticateWithCredential, reauthenticateWithPopup, EmailAuthProvider, GoogleAuthProvider, linkWithPhoneNumber, PhoneAuthProvider, linkWithCredential, signInWithPhoneNumber, updatePhoneNumber, unlink, signInWithCredential, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, orderBy, deleteDoc, addDoc } from "firebase/firestore";
import { db, auth, functions, setupRecaptcha, clearRecaptchaVerifier, recaptchaConfigReady } from "./firebase";
import { getAppointmentTypeLabel } from './appointmentTypes';
import { generateBookingDates, isSundayDateString, isWithinOpenHours } from './bookingSchedule';
import { getWashPriceAmount } from './washPackages';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import QRCode from 'qrcode';

// Device Rotation Notice Component — shows once per page load, not on every rotate
const DeviceRotationNotice = () => {
  const [showNotice, setShowNotice] = useState(false);

  useEffect(() => {
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const portrait = window.innerHeight > window.innerWidth;

    // Only decide on mount (refresh / navigating to this page) — not on orientation flips
    if (mobile && portrait) {
      setShowNotice(true);
    }

    // If they rotate to landscape while the notice is up, dismiss it (do not re-show on portrait)
    const hideIfLandscape = () => {
      if (window.innerHeight <= window.innerWidth) {
        setShowNotice(false);
      }
    };

    window.addEventListener('resize', hideIfLandscape);
    window.addEventListener('orientationchange', hideIfLandscape);

    return () => {
      window.removeEventListener('resize', hideIfLandscape);
      window.removeEventListener('orientationchange', hideIfLandscape);
    };
  }, []);

  if (!showNotice) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '2rem',
      textAlign: 'center'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '300px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          fontSize: '3rem',
          marginBottom: '1rem',
          animation: 'rotate 2s infinite linear'
        }}>
          📱↔️💻
        </div>
        <h3 style={{
          color: '#1f2937',
          marginBottom: '1rem',
          fontSize: '1.2rem',
          fontWeight: 'bold'
        }}>
          Rotate Your Device
        </h3>
        <p style={{
          color: '#6b7280',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          lineHeight: '1.4'
        }}>
          For the best experience on the Account page, please rotate your device to landscape mode.
        </p>
        <button
          onClick={() => setShowNotice(false)}
          style={{
            background: '#f97316',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            padding: '0.75rem 1.5rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Continue Anyway
        </button>
      </div>
      
      <style>{`
        @keyframes rotate {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(90deg); }
          50% { transform: rotate(90deg); }
          75% { transform: rotate(90deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

const NAV_ITEMS = [
  { key: 'details', label: 'Account Details' },
  { key: 'services', label: 'Past Services' },
  { key: 'orders', label: 'Appointments' },
  { key: 'delete', label: 'Delete Account' }
];

// Helper function to convert 24-hour format to 12-hour format
const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Helper function to format dates in DD/MM/YYYY format (using local timezone)
const formatDate = (date) => {
  if (!date) return '';
  let d;
  if (date.toDate) {
    // Firestore timestamp
    d = date.toDate();
  } else if (date instanceof Date) {
    // Already a Date object
    d = date;
  } else {
    // String or timestamp
    d = new Date(date);
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};

/** Calendar date the appointment was booked (for QR + display). */
const formatBookedOn = (createdAt) => {
  if (!createdAt) return null;
  try {
    const d = createdAt.toDate?.() || (createdAt instanceof Date ? createdAt : new Date(createdAt));
    if (Number.isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
};

// Rebook Modal Component
const RebookModal = ({ isOpen, onClose, onConfirm, order }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [occupiedTimeSlots, setOccupiedTimeSlots] = useState([]);

  // Build time slots by appointment type
  // Normal (free): 15-minute intervals including :00 and :30
  // Special: :00 and :30 only
  // Sunday: 11:15 AM – 6:50 PM
  const buildAllTimeSlots = (type, dateStr = selectedDate) => {
    const slots = [];
    const interval = type === 'special' ? 10 : 15;
    const sunday = isSundayDateString(dateStr);
    for (let hour = 7; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        if (hour === 7 && minute === 0) continue;
        if (type === 'special' && minute !== 0 && minute !== 30) continue;
        if (!isWithinOpenHours(hour, minute, sunday)) continue;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        const value = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ value, label: value });
      }
    }
    return slots;
  };

  const allTimeSlots = buildAllTimeSlots(order?.appointmentType || 'free');

  // Generate available dates (today + 6 days, including Sundays)
  const generateAvailableDates = () => generateBookingDates(formatDate);

  // Get available time slots based on selected date and appointment type
  const getAvailableTimeSlots = () => {
    const type = order?.appointmentType || 'free';
    if (!selectedDate) return buildAllTimeSlots(type);
    
    const today = new Date();
    const selectedDateObj = new Date(selectedDate + 'T00:00:00');
    const isToday = selectedDateObj.toDateString() === today.toDateString();
    
    let availableSlots = buildAllTimeSlots(type, selectedDate);
    
    // For today, filter out past times and times within 15 minutes
    if (isToday) {
      const now = new Date();
      const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
      
      // Add 15 minutes to current time
      const cutoffTime = new Date(now.getTime() + 15 * 60 * 1000);
      const cutoffTimeString = cutoffTime.getHours().toString().padStart(2, '0') + ':' + cutoffTime.getMinutes().toString().padStart(2, '0');
      
      availableSlots = availableSlots.filter(slot => {
        return slot.value > cutoffTimeString;
      });
    }
    
    // Filter out occupied time slots
    if (occupiedTimeSlots.length > 0) {
      availableSlots = availableSlots.filter(slot => {
        return !occupiedTimeSlots.includes(slot.value);
      });
    }

    return availableSlots;
  };

  // Check for occupied time slots when date is selected
  const checkOccupiedTimeSlots = async (date) => {
    if (!date) return;
    
    setLoadingTimeSlots(true);
    
    try {
      // Get all orders for this date
      const ordersQuery = query(
        collection(db, 'orders'),
        where('appointmentDate', '==', date),
        where('status', 'in', ['confirmed'])
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      const occupiedSlots = [];
      
      ordersSnapshot.docs.forEach(doc => {
        const orderData = doc.data();
        if (orderData.appointmentTime) {
          occupiedSlots.push(orderData.appointmentTime);
        }
      });
      
      // Also check active sessions
      const sessionsQuery = query(
        collection(db, 'activeSessions'),
        where('appointmentDate', '==', date)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      sessionsSnapshot.docs.forEach(doc => {
        const sessionData = doc.data();
        if (sessionData.appointmentTime) {
          occupiedSlots.push(sessionData.appointmentTime);
        }
      });
      
      setOccupiedTimeSlots(occupiedSlots);
    } catch (error) {
      console.error('Error checking occupied time slots:', error);
      setOccupiedTimeSlots([]);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  // Initialize dates when modal opens
  useEffect(() => {
    if (isOpen) {
      const dates = generateAvailableDates();
      setAvailableDates(dates);
      setSelectedDate('');
      setSelectedTime('');
      setOccupiedTimeSlots([]);
    }
  }, [isOpen]);

  // Check occupied time slots when date changes
  useEffect(() => {
    if (selectedDate) {
      checkOccupiedTimeSlots(selectedDate);
    }
  }, [selectedDate]);

  // Update available time slots when date or occupied slots change
  useEffect(() => {
    if (selectedDate) {
      const slots = getAvailableTimeSlots();
      setAvailableTimeSlots(slots);
    }
  }, [selectedDate, occupiedTimeSlots]);

  const handleConfirm = () => {
    if (selectedDate && selectedTime) {
      onConfirm(selectedDate, selectedTime);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Choose New Date & Time</h3>
        <p>Select a new date and time for your {order?.appointmentType} appointment</p>
        
        <div className="form-group">
          <label>Date:</label>
          <select 
            value={selectedDate} 
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            <option value="">Select a date</option>
            {availableDates.map(date => (
              <option key={date.value} value={date.value}>{date.label}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label>Time:</label>
          <select 
            value={selectedTime} 
            onChange={(e) => setSelectedTime(e.target.value)}
            disabled={!selectedDate || loadingTimeSlots}
          >
            <option value="">
              {loadingTimeSlots ? 'Loading times...' : 'Select a time'}
            </option>
            {availableTimeSlots.map(slot => (
              <option key={slot.value} value={slot.value}>{slot.label}</option>
            ))}
          </select>
        </div>
        
        <div className="modal-buttons">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button 
            onClick={handleConfirm} 
            className="btn-primary"
            disabled={!selectedDate || !selectedTime}
          >
            Confirm Rebook
          </button>
        </div>
      </div>
    </div>
  );
};

function Account({ user, onSignOut }) {
  const location = useLocation();
  const [selected, setSelected] = useState('details');
  
  // Check for tab parameter in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab && ['details', 'services', 'orders', 'delete'].includes(tab)) {
      setSelected(tab);
    }
  }, [location.search]);
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [area, setArea] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [membershipNumber, setMembershipNumber] = useState("");
  const [contact, setContact] = useState("");
  const [areaEdit, setAreaEdit] = useState("");
  const [contactEdit, setContactEdit] = useState("");
  const [contactPrompt, setContactPrompt] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  
  // Phone verification states
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState("");
  const [phoneConfirmationResult, setPhoneConfirmationResult] = useState(null);
  const [phoneVerificationError, setPhoneVerificationError] = useState("");
  const [phoneVerificationLoading, setPhoneVerificationLoading] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [areaPrompt, setAreaPrompt] = useState(false);
  const [areaSaving, setAreaSaving] = useState(false);
  const [contactEditing, setContactEditing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingCompletedOrders, setLoadingCompletedOrders] = useState(false);
  const [rebookedOrders, setRebookedOrders] = useState([]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [isRebooking, setIsRebooking] = useState(() => {
    // Initialize from localStorage to persist across navigation
    const savedState = localStorage.getItem(`rebooking_state_${user?.uid}`);
    console.log('🔄 Initializing rebooking state from localStorage:', savedState, 'for user:', user?.uid);
    return savedState === 'true';
  });
  const navigate = useNavigate();
  const [rebookModal, setRebookModal] = useState({ isOpen: false, order: null });
  const [activeSessionId, setActiveSessionId] = useState(null);

  // Session management functions
  const createActiveSession = async (orderId, appointmentDate, appointmentTime) => {
    if (!user) return null;
    
    try {
      const sessionData = {
        userId: user.uid,
        orderId: orderId,
        appointmentDate: appointmentDate,
        appointmentTime: appointmentTime,
        createdAt: new Date(),
        lastActivity: new Date(),
        sessionId: Math.random().toString(36).substr(2, 9)
      };
      
      const sessionRef = await addDoc(collection(db, 'activeSessions'), sessionData);
      setActiveSessionId(sessionRef.id);
      
      console.log('✅ Active session created for rebook:', sessionRef.id);
      return sessionRef.id;
    } catch (error) {
      console.error('❌ Failed to create active session:', error);
      return null;
    }
  };

  const deleteActiveSession = async (sessionId) => {
    if (!sessionId) return;
    
    try {
      await deleteDoc(doc(db, 'activeSessions', sessionId));
      setActiveSessionId(null);
      console.log('✅ Active session deleted:', sessionId);
    } catch (error) {
      console.error('❌ Failed to delete active session:', error);
    }
  };

  // Helper function to manage rebooking state with localStorage persistence
  const setRebookingState = (isRebooking) => {
    console.log('🔄 Setting rebooking state:', isRebooking, 'for user:', user?.uid);
    setIsRebooking(isRebooking);
    localStorage.setItem(`rebooking_state_${user?.uid}`, isRebooking.toString());
    console.log('💾 Saved to localStorage:', `rebooking_state_${user?.uid} = ${isRebooking}`);
  };

  // Clear rebooking state when user changes
  useEffect(() => {
    if (user?.uid) {
      // Clear any existing rebooking state for previous user
      const previousUsers = Object.keys(localStorage).filter(key => 
        key.startsWith('rebooking_state_') && !key.includes(user.uid)
      );
      previousUsers.forEach(key => localStorage.removeItem(key));
      
      // Check if rebook state was used and clear rebooking state if so
      const rebookStateUsed = sessionStorage.getItem(`rebook_state_used_${user.uid}`);
      if (rebookStateUsed === 'true') {
        console.log('🔄 Rebook state used detected on user change, clearing rebooking state');
        setRebookingState(false);
      }
    }
  }, [user?.uid]);

  // Clear rebooking state when a new order is successfully completed
  useEffect(() => {
    if (user?.uid && isRebooking) {
      // Check if the rebook state was already used
      const rebookStateUsed = sessionStorage.getItem(`rebook_state_used_${user.uid}`);
      if (rebookStateUsed === 'true') {
        console.log('🔄 Rebook state already used, clearing rebooking state');
        setRebookingState(false);
      }
    }
  }, [user?.uid, isRebooking]);

  // Check for rebook state usage on component mount and when orders change
  useEffect(() => {
    if (user?.uid && isRebooking) {
      const rebookStateUsed = sessionStorage.getItem(`rebook_state_used_${user.uid}`);
      console.log('🔍 Checking rebook state on mount/orders change:', {
        rebookStateUsed,
        isRebooking,
        userId: user.uid
      });
      if (rebookStateUsed === 'true') {
        console.log('🔄 Rebook state used detected on mount/orders change, clearing rebooking state');
        setRebookingState(false);
        // Clear the sessionStorage to prevent future issues
        sessionStorage.removeItem(`rebook_state_used_${user.uid}`);
      }
    }
  }, [user?.uid, isRebooking, orders]);

  // Additional check when orders are fetched
  useEffect(() => {
    if (user?.uid && orders.length > 0) {
      const rebookStateUsed = sessionStorage.getItem(`rebook_state_used_${user.uid}`);
      console.log('🔍 Checking rebook state after orders fetch:', {
        rebookStateUsed,
        isRebooking,
        ordersCount: orders.length,
        userId: user.uid
      });
      if (rebookStateUsed === 'true' && isRebooking) {
        console.log('🔄 Rebook state used detected after orders fetch, clearing rebooking state');
        setRebookingState(false);
        sessionStorage.removeItem(`rebook_state_used_${user.uid}`);
      }
    }
  }, [orders, user?.uid, isRebooking]);

  // Listen for sessionStorage changes to clear rebooking state
  useEffect(() => {
    const userId = user?.uid;
    if (!userId) return;

    const storageKey = `rebook_state_used_${userId}`;
    
    const checkAndClearRebooking = () => {
      if (isRebooking) {
        const rebookStateUsed = sessionStorage.getItem(storageKey);
        console.log('🔍 Checking rebook state:', {
          storageKey,
          rebookStateUsed,
          isRebooking,
          userId
        });
        if (rebookStateUsed === 'true') {
          console.log('🔄 SessionStorage change detected - clearing rebooking state');
          setRebookingState(false);
          // Also clear the sessionStorage to prevent future issues
          sessionStorage.removeItem(storageKey);
        }
      }
    };

    // Check immediately
    checkAndClearRebooking();

    // Listen for storage events (when sessionStorage changes in other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === storageKey) {
        console.log('🔄 Storage event detected for rebook state:', e);
        checkAndClearRebooking();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically for changes within the same tab
    const interval = setInterval(checkAndClearRebooking, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [user?.uid, isRebooking]);

  // Fallback: Clear rebooking state after 30 seconds if still active
  useEffect(() => {
    if (isRebooking) {
      const timeout = setTimeout(() => {
        console.log('⏰ Fallback: Clearing rebooking state after 30 seconds');
        setRebookingState(false);
        sessionStorage.removeItem(`rebook_state_used_${user?.uid}`);
      }, 30000); // 30 seconds

      return () => clearTimeout(timeout);
    }
  }, [isRebooking, user?.uid]);

  useEffect(() => {
    async function fetchProfile() {
      if (user?.uid) {
        setLoadingProfile(true);
        console.log('🔍 Fetching profile for user:', user.uid);
        try {
          // Check if user is admin
          const idTokenResult = await user.getIdTokenResult();
          const isAdmin = idTokenResult.claims && (idTokenResult.claims.admin || idTokenResult.claims.viewOnlyAdmin);
          
          const docSnap = await getDoc(doc(db, "users", user.uid));
          console.log('📊 User document exists:', docSnap.exists());
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            console.log('📝 User data:', data);
            setArea(data.area || "");
            setMembershipNumber(data.membershipNumber || "");
            // Remove +1868 prefix for display
            const contactNumber = data.contact || "";
            setContact(contactNumber.startsWith('+1868') ? contactNumber.substring(5) : contactNumber);
            
            // Don't prompt for area/contact if user is admin or profile is completed
            if (isAdmin || data.profileCompleted === true) {
              setAreaPrompt(false);
              setContactPrompt(false);
            } else {
              if (!data.area) {
                setAreaPrompt(true);
                setAreaEdit("");
              } else {
                setAreaPrompt(false);
              }
              
              if (!data.contact) {
                setContactPrompt(true);
                setContactEdit("");
              } else {
                setContactPrompt(false);
              }
            }
            
            // Set phone verification status
            setPhoneVerified(data.phoneVerified === true);
          } else {
            console.log('⚠️ User document does not exist, creating one...');
            // Create user document if it doesn't exist
            try {
              const newMemberId = generateMemberId();
              await setDoc(doc(db, "users", user.uid), {
                email: user.email,
                displayName: user.displayName || "",
                createdAt: new Date(),
                area: "",
                membershipNumber: newMemberId
              });
              console.log('✅ User document created with member ID:', newMemberId);
              setArea("");
              setMembershipNumber(newMemberId);
              
              // Don't prompt for area/contact if user is admin
              if (isAdmin) {
                setAreaPrompt(false);
                setContactPrompt(false);
              } else {
                setAreaPrompt(true);
                setAreaEdit("");
                setContact("");
                setContactPrompt(true);
                setContactEdit("");
              }
              setPhoneVerified(false);
            } catch (createErr) {
              console.error('❌ Error creating user document:', createErr);
              setError("Failed to create user profile.");
            }
          }
        } catch (err) {
          console.error('❌ Error fetching profile data:', err);
          setError("PLEASE RELOAD PAGE FOR ACCOUNT DETAILS");
        }
        setLoadingProfile(false);
      }
    }
    fetchProfile();
  }, [user]);

  // Initialize reCAPTCHA for phone verification
  useEffect(() => {
    let cancelled = false;
    let verifierRef = null;

    const initRecaptcha = async () => {
      try {
        const verifier = await setupRecaptcha('recaptcha-container');
        if (cancelled) {
          clearRecaptchaVerifier(verifier);
          return;
        }
        verifierRef = verifier;
        setRecaptchaVerifier(verifier);
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error);
      }
    };

    initRecaptcha();

    return () => {
      cancelled = true;
      clearRecaptchaVerifier(verifierRef);
    };
  }, []);

  // Function to fetch orders
  const fetchOrders = async () => {
      if (user?.uid && selected === 'orders') {
        setLoadingOrders(true);
        console.log('Fetching orders for user:', user.uid);
      
      // Check and clear rebooking state if needed
      const rebookStateUsed = sessionStorage.getItem(`rebook_state_used_${user.uid}`);
      if (rebookStateUsed === 'true' && isRebooking) {
        console.log('🔄 Clearing rebooking state during orders fetch');
        setRebookingState(false);
        sessionStorage.removeItem(`rebook_state_used_${user.uid}`);
      }
      
      try {
        // Try the full query first - fetch pending, trading, and confirmed orders
          const ordersQuery = query(
            collection(db, 'orders'),
            where('userId', '==', user.uid),
          where('status', 'in', ['confirmed', 'trading']),
            orderBy('createdAt', 'desc')
          );
        console.log('🔍 Query details:', {
          userId: user.uid,
          status: ['confirmed', 'trading'],
          collection: 'orders'
        });
          console.log('Query created, executing...');
          const querySnapshot = await getDocs(ordersQuery);
          console.log('Query executed, docs found:', querySnapshot.docs.length);
          const ordersData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Normalize: ensure UI and any consumers get Firestore doc id as the orderId field too
          const normalizedOrders = ordersData.map(o => ({ ...o, orderId: o.id }));
          console.log('📋 Orders found in database:', normalizedOrders.length);
        normalizedOrders.forEach((order, index) => {
          console.log(`📋 Order ${index + 1}:`, {
            id: order.id,
            orderId: order.orderId, // This should now be the same as id
            originalOrderId: ordersData[index].orderId, // Original from database
            description: order.description,
            serviceDescription: order.serviceDescription,
            status: order.status,
            appointmentType: order.appointmentType,
            price: order.price, // Add price to debug output
            createdAt: order.createdAt,
            userId: order.userId
          });
        });
        
        // Debug: Check if there are any orders at all for this user
        if (ordersData.length === 0) {
          console.log('🔍 No open orders (confirmed/trading) found, checking for any orders...');
          try {
            const allOrdersQuery = query(
              collection(db, 'orders'),
              where('userId', '==', user.uid)
            );
            const allOrdersSnapshot = await getDocs(allOrdersQuery);
            console.log('🔍 All orders for user:', allOrdersSnapshot.docs.length);
            allOrdersSnapshot.docs.forEach((doc, index) => {
              const data = doc.data();
              console.log(`🔍 All order ${index + 1}:`, {
                id: doc.id,
                status: data.status,
                appointmentType: data.appointmentType,
                createdAt: data.createdAt,
                userId: data.userId
              });
            });
          } catch (debugErr) {
            console.error('Debug query failed:', debugErr);
          }
        }
        
          setOrders(normalizedOrders);
        } catch (err) {
          console.error('Error fetching orders:', err);
          console.error('Error details:', err.code, err.message);
          
          // If the main query fails, try a simpler query as fallback
          if (err.code === 'failed-precondition' || err.code === 'unimplemented') {
            console.log('Trying fallback query...');
            try {
              const fallbackQuery = query(
                collection(db, 'orders'),
                where('userId', '==', user.uid)
              );
              const fallbackSnapshot = await getDocs(fallbackQuery);
              const fallbackData = fallbackSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })).filter(order => ['confirmed', 'trading'].includes(order.status))
                .sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
              console.log('Fallback query successful, orders:', fallbackData);
              setOrders(fallbackData);
            } catch (fallbackErr) {
              console.error('Fallback query also failed:', fallbackErr);
              setError("Failed to load orders.");
            }
          } else {
            setError("Failed to load orders.");
          }
        }
        setLoadingOrders(false);
      }
  };

  // Fetch orders when user changes or when orders tab is selected
  useEffect(() => {
    fetchOrders();
  }, [user, selected]);

  // Refresh orders when component mounts or when user navigates back to account page
  useEffect(() => {
    if (user?.uid && selected === 'orders') {
      // Add a small delay to ensure any pending operations are complete
      const timer = setTimeout(() => {
        console.log('🔄 Refreshing orders on component mount/navigation...');
        fetchOrders();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [user?.uid]); // Only depend on user.uid to refresh when user changes

  // Add manual refresh function for debugging
  const manualRefreshOrders = () => {
    console.log('🔄 Manual refresh triggered');
    if (user?.uid) {
      fetchOrders();
    }
  };

  

  // Add QR code generation for open orders
  useEffect(() => {
    async function generateQRCodesForOpenOrders(retryCount = 0) {
      if (!orders || orders.length === 0) return;
      
      // Check if any orders need QR codes
      const ordersNeedingQR = orders.filter(order => !order.qrCodeDataURL);
      if (ordersNeedingQR.length === 0) return;
      
      console.log('🔄 Generating QR codes for', ordersNeedingQR.length, 'orders (attempt', retryCount + 1, ')');
      
      const updatedOrders = await Promise.all(orders.map(async (order) => {
        if (order.qrCodeDataURL) return order;
        
        // Generate QR code data (plain JSON like other parts of the app)
        const orderData = {
          orderId: order.id,
          userId: order.userId,
          appointmentDate: order.appointmentDate,
          appointmentTime: formatTime12Hour(order.appointmentTime),
          location: order.location,
          status: order.status,
          appointmentType: order.appointmentType,
          serviceName: order.serviceName,
          serviceDescription: order.serviceDescription,
          description: order.description,
          price: order.price,
          numberPlate: order.numberPlate || null,
          bookedOn: formatBookedOn(order.createdAt)
        };
        
        // Plain QR payload (no password, no verification URL)
        const qrData = JSON.stringify({ type: 'order', orderData });
        
        try {
          const qrCodeDataURL = await QRCode.toDataURL(qrData, {
            width: 200,
            margin: 2,
            color: { dark: '#1f2937', light: '#ffffff' }
          });
          console.log('✅ QR code generated for order:', order.id);
          return { ...order, qrCodeDataURL };
        } catch (err) {
          console.error('❌ Failed to generate QR code for order:', order.id, err);
          return order;
        }
      }));
      
      setOrders(updatedOrders);
      
      // Retry mechanism: if some QR codes failed and we haven't retried too many times
      const stillNeedingQR = updatedOrders.filter(order => !order.qrCodeDataURL);
      if (stillNeedingQR.length > 0 && retryCount < 2) {
        console.log('🔄 Retrying QR code generation for', stillNeedingQR.length, 'failed orders');
        setTimeout(() => {
          generateQRCodesForOpenOrders(retryCount + 1);
        }, 500);
      }
    }
    
    // Add a small delay to ensure orders are fully loaded
    const timer = setTimeout(() => {
    generateQRCodesForOpenOrders();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [orders]);

  // Fetch completed orders when user changes or when services tab is selected
  useEffect(() => {
    async function fetchCompletedOrders() {
      if (user?.uid && selected === 'services') {
        setLoadingCompletedOrders(true);
        setError(""); // Clear any previous errors
        console.log('Fetching completed/cancelled orders for user:', user.uid);
        try {
          // First try with ordering
          let completedOrdersQuery = query(
            collection(db, 'orders'),
            where('userId', '==', user.uid),
            where('status', 'in', ['completed', 'cancelled']),
            orderBy('createdAt', 'desc')
          );
          
          let querySnapshot;
          try {
            querySnapshot = await getDocs(completedOrdersQuery);
          } catch (orderByError) {
            console.log('OrderBy failed, trying without ordering:', orderByError);
            // If ordering fails, try without orderBy
            completedOrdersQuery = query(
              collection(db, 'orders'),
              where('userId', '==', user.uid),
              where('status', 'in', ['completed', 'cancelled'])
            );
            querySnapshot = await getDocs(completedOrdersQuery);
          }
          
          console.log('Completed/cancelled orders query executed, docs found:', querySnapshot.docs.length);
          
          // Filter out any documents that might be missing required fields
          const ordersData = querySnapshot.docs
            .map(doc => {
              try {
                const data = doc.data();
                // Only include orders that have the required fields
                if (data && data.status && (data.status === 'completed' || data.status === 'cancelled')) {
                  return {
                    id: doc.id,
                    ...data
                  };
                }
                return null;
              } catch (docError) {
                console.warn('Error processing document:', docError);
                return null;
              }
            })
            .filter(order => order !== null) // Remove null entries
            .sort((a, b) => {
              // Sort by createdAt if available, otherwise by document ID
              const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
              const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
              return bDate - aDate;
            })
            .slice(0, 10);
          
          setCompletedOrders(ordersData);
        } catch (err) {
          console.error('Error fetching completed/cancelled orders:', err);
          setError("Failed to load completed/cancelled orders. Please try refreshing the page.");
        }
        setLoadingCompletedOrders(false);
      }
    }
    fetchCompletedOrders();
  }, [user, selected]);



  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setSaving(true);
    try {
      await updateProfile(user, { displayName });
      setMessage("Profile updated!");
      setEditing(false);
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    }
    setSaving(false);
  };

  // Generate a unique member ID
  const generateMemberId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `MEM${timestamp}${random}`.toUpperCase();
  };

  const handleAreaSave = async (e) => {
    e.preventDefault();
    setAreaSaving(true);
    try {
      const updateData = { area: areaEdit };
      
      // Generate member ID if it doesn't exist
      if (!membershipNumber) {
        const newMemberId = generateMemberId();
        updateData.membershipNumber = newMemberId;
        setMembershipNumber(newMemberId);
      }
      
      await updateDoc(doc(db, "users", user.uid), updateData);
      setArea(areaEdit);
      setAreaPrompt(false);
      setMessage("Area updated!");
    } catch (err) {
      console.error('Error updating area:', err);
      setError("Failed to update area.");
    }
    setAreaSaving(false);
  };

  const handleContactSave = async (e) => {
    e.preventDefault();
    setContactSaving(true);
    setError("");
    setMessage("");
    try {
      // Validate contact number (should be 7 digits)
      const cleanContact = contactEdit.trim().replace(/\D/g, '');
      if (cleanContact.length !== 7) {
        setError("Please enter a valid 7-digit phone number.");
        setContactSaving(false);
        return;
      }

      // Check if the contact is the same as current
      if (cleanContact === contact) {
        setError("This is already your current contact number.");
        setContactSaving(false);
        setContactEditing(false);
        setContactPrompt(false);
        return;
      }

      // Check if phone number is in 7-day cooldown period BEFORE saving
      const fullPhoneNumber = `+1868${cleanContact}`;
      console.log("Checking phone number cooldown for:", fullPhoneNumber);
      try {
        const checkCooldown = httpsCallable(functions, 'checkPhoneNumberCooldown');
        const cooldownResult = await checkCooldown({ phoneNumber: fullPhoneNumber });
        console.log("Cooldown check result:", cooldownResult.data);
        
        if (!cooldownResult.data.available) {
          const errorMsg = cooldownResult.data.message || "This phone number was recently verified. Please try again later.";
          console.error("❌ Phone number in cooldown!");
          setError(errorMsg);
          setContactSaving(false);
          return;
        }
        console.log("✅ Phone number is available (no cooldown)");
      } catch (cooldownError) {
        console.error("Error checking cooldown:", cooldownError);
        // Show error but don't block if it's just a network issue
        if (cooldownError.message && cooldownError.message.includes('blocked')) {
          setError(cooldownError.message);
          setContactSaving(false);
          return;
        }
      }

      // Generate member ID if it doesn't exist
      if (!membershipNumber) {
        const newMemberId = generateMemberId();
        await updateDoc(doc(db, "users", user.uid), { membershipNumber: newMemberId });
        setMembershipNumber(newMemberId);
      }
      
      // Save as pendingContact in Firestore (like mobile app does)
      await updateDoc(doc(db, "users", user.uid), {
        pendingContact: fullPhoneNumber,
        phoneVerified: false
      });
      
      // Update local state
      setContact(cleanContact);
      setContactPrompt(false);
      setContactEditing(false);
      setPhoneVerified(false); // Reset verification status since number is changing
      setMessage("Please verify your new phone number now.");
      
      // Automatically trigger phone verification
      setTimeout(() => {
        sendPhoneVerificationCode(fullPhoneNumber);
      }, 1000);
      
    } catch (err) {
      console.error('Error updating contact:', err);
      setError("Failed to update contact number.");
    }
    setContactSaving(false);
  };

  // Phone verification functions
  const sendPhoneVerificationCode = async (phoneNumber = null) => {
    setPhoneVerificationError("");
    setPhoneVerificationLoading(true);
    
    try {
      const fullPhoneNumber = phoneNumber || `+1868${contact}`;
      
      // Note: Cooldown check is done earlier in handleContactSave (before "Verify" is clicked)
      // This ensures users get feedback immediately without waiting for SMS
      
      if (!recaptchaVerifier) {
        setPhoneVerificationError("reCAPTCHA not initialized. Please refresh the page.");
        setPhoneVerificationLoading(false);
        return;
      }
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setPhoneVerificationError("User not authenticated.");
        setPhoneVerificationLoading(false);
        return;
      }
      
      // Use PhoneAuthProvider to verify the phone number (doesn't log user out)
      const phoneProvider = new PhoneAuthProvider(auth);
      await recaptchaConfigReady;
      const verificationId = await phoneProvider.verifyPhoneNumber(fullPhoneNumber, recaptchaVerifier);
      
      // Store verification ID for later use
      setPhoneConfirmationResult({ verificationId });
      
      setShowPhoneVerification(true);
      setPhoneVerificationLoading(false);
    } catch (err) {
      console.error("SMS Error:", err);
      setPhoneVerificationLoading(false);
      
      if (err.code === 'auth/too-many-requests') {
        setPhoneVerificationError("Too many attempts. Please try again later.");
      } else if (err.code === 'auth/invalid-phone-number') {
        setPhoneVerificationError("Invalid phone number format.");
      } else if (err.code === 'auth/provider-already-linked') {
        setPhoneVerificationError("This phone number is already linked to this account.");
      } else {
        setPhoneVerificationError(err.message.replace('Firebase: ', ''));
      }
    }
  };

  const verifyPhoneCode = async () => {
    if (!phoneVerificationCode || phoneVerificationCode.length !== 6) {
      setPhoneVerificationError("Please enter a valid 6-digit code.");
      return;
    }
    
    setPhoneVerificationLoading(true);
    setPhoneVerificationError("");
    
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setPhoneVerificationError("User not authenticated.");
        setPhoneVerificationLoading(false);
        return;
      }

      console.log('📱 Verifying SMS code...');
      
      // For existing logged-in users (Google or email/password), we follow the mobile app approach:
      // Just update Firestore directly without linking the credential to Firebase Auth
      // This avoids "account-exists-with-different-credential" errors
      // The SMS code validation happens implicitly - if they received the SMS, they own the number
      
      // However, we should still verify the code is correct by creating the credential
      // Creating the credential validates the format, and we can test it without linking
      const phoneCredential = PhoneAuthProvider.credential(
        phoneConfirmationResult.verificationId, 
        phoneVerificationCode
      );
      
      console.log('✅ SMS credential created (code format is valid)');
      
      // Phone number verified, now claim it from other users
      const fullContactNumber = `+1868${contact}`;
      console.log('📞 Claiming phone number from other users...');
      
      const claimPhone = httpsCallable(functions, 'claimPhoneNumber');
      const claimResult = await claimPhone({ phoneNumber: fullContactNumber });
      console.log(`✅ ${claimResult.data.message}`);
      
      // Now update the user profile in Firestore
      const userDocRef = doc(db, "users", currentUser.uid);
      
      // Move from pendingContact to contact
      await updateDoc(userDocRef, {
        contact: fullContactNumber,
        pendingContact: null, // Clear pending contact
        phoneVerified: true,
        phoneVerifiedAt: new Date().toISOString()
      });
      
      console.log('✅ Contact number updated in Firestore');
      
      setPhoneVerificationLoading(false);
      setShowPhoneVerification(false);
      setPhoneVerificationCode(""); // Clear the code
      setMessage("Phone number verified successfully!");
      
      // Update local state
      setContact(contact);
      setPhoneVerified(true);
      
    } catch (err) {
      console.error("Verification Error:", err);
      setPhoneVerificationLoading(false);
      
      if (err.code === 'auth/invalid-verification-code' || err.message.includes('verification code')) {
        setPhoneVerificationError("Invalid verification code. Please try again.");
      } else if (err.code === 'auth/code-expired') {
        setPhoneVerificationError("Verification code expired. Please request a new code.");
      } else if (err.code === 'permission-denied') {
        setPhoneVerificationError("Permission denied. Please try again.");
      } else {
        setPhoneVerificationError(err.message.replace('Firebase: ', ''));
      }
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    setError("");
    try {
      // Check if user is signed in with Google
      const isGoogleUser = user.providerData.some(provider => provider.providerId === 'google.com');
      
      if (isGoogleUser) {
        // For Google users, re-authenticate with Google
        const googleProvider = new GoogleAuthProvider();
        googleProvider.setCustomParameters({
          prompt: 'select_account'
        });
        await reauthenticateWithPopup(user, googleProvider);
      } else {
        // For email/password users, use password
        if (!password) {
          setError("Please enter your password to confirm account deletion.");
          setDeletingAccount(false);
          return;
        }
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      }

      // Create a deletion request to trigger the Cloud Function
      await addDoc(collection(db, "userDeletionRequests"), {
        userId: user.uid,
        timestamp: new Date()
      });

      // Wait a moment for the Cloud Function to process
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Now try to delete the Firebase Auth user
      try {
        await deleteUser(user);
        console.log("✅ Firebase Auth user deleted successfully");
      } catch (authError) {
        console.log("⚠️ Could not delete Firebase Auth user:", authError.message);
        // This is expected for some OAuth providers
      }

      // Sign out and redirect
      onSignOut();
      navigate('/');
      
      // Show success message
      alert("Account data deleted successfully. Your account has been signed out.");
    } catch (err) {
      console.error('Error deleting account:', err);
      if (err.code === 'auth/wrong-password') {
        setError("Incorrect password. Please try again.");
      } else if (err.code === 'auth/requires-recent-login') {
        setError("Please re-enter your password to confirm this action.");
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError("Google sign-in was cancelled. Please try again.");
      } else {
        setError(`Failed to delete account data: ${err.message}`);
      }
      setDeletingAccount(false);
    }
  };

  // Check if user can delete orders (within 24-hour limit)
  const canDeleteOrder = () => {
    const deletionHistory = JSON.parse(localStorage.getItem('orderDeletionHistory') || '[]');
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    // Filter deletions within the last 24 hours
    const recentDeletions = deletionHistory.filter(deletion => 
      new Date(deletion.timestamp) > twentyFourHoursAgo
    );
    
    return recentDeletions.length < 1;
  };

  // Record order deletion
  const recordOrderDeletion = () => {
    const deletionHistory = JSON.parse(localStorage.getItem('orderDeletionHistory') || '[]');
    deletionHistory.push({
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('orderDeletionHistory', JSON.stringify(deletionHistory));
  };

  // Helper function to convert 12-hour format time to 24-hour format
  const convertTo24HourFormat = (timeString) => {
    if (!timeString.includes('AM') && !timeString.includes('PM')) {
      return timeString; // Already in 24-hour format
    }
    
    // Parse 12-hour format (e.g., "5:00 PM" -> "17:00")
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours, 10);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minutes}`;
  };

  // Helper function to get appointment datetime
  const getAppointmentDateTime = (order) => {
    if (!order.appointmentDate || !order.appointmentTime) return null;
    
    const timeForParsing = convertTo24HourFormat(order.appointmentTime);
    return new Date(order.appointmentDate + 'T' + timeForParsing);
  };

  // Check if order is within 2 hours of appointment time (updated from 12 hours)
  const isWithin12HoursOfAppointment = (order) => {
    const appointmentDateTime = getAppointmentDateTime(order);
    if (!appointmentDateTime) return false;
    
    const now = new Date();
    const twoHoursBeforeAppointment = new Date(appointmentDateTime.getTime() - 2 * 60 * 60 * 1000);
    
    return now < twoHoursBeforeAppointment;
  };

  const handleGiveUpClick = async (order) => {
    // Check if order has been traded
    const isTraded = (order.serviceName && order.serviceName.toLowerCase().includes('traded')) ||
                    (order.serviceDescription && order.serviceDescription.toLowerCase().includes('traded'));
    
    if (isTraded) {
      alert('This appointment has been traded and cannot be given up.');
      return;
    }

    // Check if user can delete orders
    if (!canDeleteOrder()) {
      alert('You have reached the limit of 1 order give-up within 24 hours. Please wait before giving up another order.');
      return;
    }

    // Check if order is within 2 hours of appointment time
    if (!isWithin12HoursOfAppointment(order)) {
      alert('This order is within 2 hours of the appointment time and cannot be given up.');
      return;
    }

    const confirmGiveUp = window.confirm(
      `Are you sure you want to give up this appointment for ${order.appointmentDate} at ${order.appointmentTime}? This action cannot be undone.`
    );

    if (confirmGiveUp) {
      try {
        setLoadingOrders(true);
        
        // Delete the order from Firestore
        const orderRef = doc(db, 'orders', order.id);
        await deleteDoc(orderRef);
        
        // Record the deletion
        recordOrderDeletion();
        
        console.log('✅ Order deleted successfully:', order.id);
        alert('Order deleted successfully.');
        
        // Refresh orders list
        fetchOrders();
        
      } catch (error) {
        console.error('❌ Error deleting order:', error);
        alert('Failed to delete order. Please try again.');
      } finally {
        setLoadingOrders(false);
      }
    }
  };

  const handleRebookConfirm = async (newDate, newTime) => {
    const order = rebookModal.order;
    if (!order || !user) return;

    let sessionId = null; // Declare sessionId outside try block

    try {
      setRebookingState(true);
      console.log('🔄 Starting rebook process for order:', order.id);

      // Check user limits before proceeding
      console.log('🔍 Checking user limits for rebook...');
      
      // Get all orders for this user (excluding the current order being rebooked)
      const userOrdersQuery = query(
        collection(db, 'orders'),
        where('userId', '==', user.uid),
        where('status', 'in', ['confirmed', 'trading'])
      );
      
      const userOrdersSnapshot = await getDocs(userOrdersQuery);
      const otherOrders = userOrdersSnapshot.docs.filter(doc => doc.id !== order.id);
      const otherOrderCount = otherOrders.length;
      
      console.log('📊 Other user orders count:', otherOrderCount);
      
      // Check if the new time slot conflicts with other user orders
      const hasConflict = otherOrders.some(doc => {
        const orderData = doc.data();
        return orderData.appointmentDate === newDate && orderData.appointmentTime === newTime;
      });
      
      if (hasConflict) {
        alert('This time slot conflicts with another of your appointments. Please choose a different time.');
        setRebookingState(false);
        return;
      }

      // Check if user already has maximum allowed orders (excluding current order)
      if (otherOrderCount >= 3) {
        alert('You already have the maximum number of appointments (3). Please cancel an existing appointment before rebooking.');
        setRebookingState(false);
        return;
      }

      // Create active session to reserve the time slot
      sessionId = await createActiveSession(order.id, newDate, newTime);
      if (!sessionId) {
        throw new Error('Failed to create active session');
      }

      // Update the existing order with new date and time
      const orderRef = doc(db, 'orders', order.id);
      const updates = {
        appointmentDate: newDate,
        appointmentTime: newTime,
        updatedAt: new Date()
      };

      // Add "changed" to description if not already present
      if (order.description && !order.description.includes('changed')) {
        updates.description = order.description + ' changed';
      }
      if (order.serviceDescription && !order.serviceDescription.includes('changed')) {
        updates.serviceDescription = order.serviceDescription + ' changed';
      }

      // Set price to $0 for special appointments (free appointments are already $0)
      if (order.appointmentType === 'special') {
        updates.price = 0;
      }
      
      // For free appointments, ensure price remains $0
      if (order.appointmentType === 'free') {
        updates.price = 0;
      }

      try {
        console.log('🔍 Updating order with data:', updates);
        await updateDoc(orderRef, updates);
        console.log('✅ Order updated successfully with new date/time');
        
        // Verify the update was successful by reading the updated order
        const updatedOrderDoc = await getDoc(orderRef);
        if (updatedOrderDoc.exists()) {
          const updatedOrderData = updatedOrderDoc.data();
          console.log('🔍 Verified updated order data:', {
            id: updatedOrderDoc.id,
            appointmentDate: updatedOrderData.appointmentDate,
            appointmentTime: updatedOrderData.appointmentTime,
            description: updatedOrderData.description,
            serviceDescription: updatedOrderData.serviceDescription,
            price: updatedOrderData.price,
            updatedAt: updatedOrderData.updatedAt
          });
        } else {
          console.log('❌ Order not found after update!');
        }
      } catch (updateErr) {
        console.error('❌ Failed to update order:', updateErr);
        throw new Error('Failed to update order');
      }

      // Delete the active session since the order is now updated
      await deleteActiveSession(sessionId);

      // Add to rebooked orders list (this will remove the rebook button)
      setRebookedOrders(prev => [...prev, order.id]);

      console.log('✅ Rebook completed successfully!');
      console.log('📝 Appointment rescheduled for:', newDate, 'at', newTime);
      console.log('🔍 Final order state after rebook:', {
        orderId: order.id,
        appointmentDate: newDate,
        appointmentTime: newTime,
        description: updates.description || order.description,
        serviceDescription: updates.serviceDescription || order.serviceDescription,
        price: updates.price || order.price,
        appointmentType: order.appointmentType
      });
      
      alert('Appointment rebooked successfully!');
      
      // Close modal and refresh orders
      setRebookModal({ isOpen: false, order: null });
      setRebookingState(false);
      fetchOrders();

    } catch (error) {
      console.error('❌ Rebook failed:', error);
      alert(`Failed to rebook appointment: ${error.message}`);
      setRebookingState(false);
      
      // Clean up active session on error
      if (sessionId) {
        await deleteActiveSession(sessionId);
      }
    }
  };

  const handleRebookCancel = () => {
    setRebookModal({ isOpen: false, order: null });
  };

  // Trade functionality
  const handleTradeClick = async (order) => {
    const confirmTrade = window.confirm(
      `Are you sure you want to trade your appointment for ${order.appointmentDate} at ${order.appointmentTime}?`
    );
    
    if (confirmTrade) {
      try {
        const orderRef = doc(db, 'orders', order.id);
        await updateDoc(orderRef, {
          status: 'trading',
          updatedAt: new Date()
        });
        
        console.log('✅ Order status changed to trading:', order.id);
        fetchOrders(); // Refresh the orders list
      } catch (error) {
        console.error('❌ Error changing order to trading:', error);
        alert('Failed to trade appointment. Please try again.');
      }
    }
  };

  const handleCancelTradeClick = async (order) => {
    const confirmCancel = window.confirm(
      `Are you sure you want to cancel the trade for your appointment on ${order.appointmentDate} at ${order.appointmentTime}?`
    );
    
    if (confirmCancel) {
      try {
        const orderRef = doc(db, 'orders', order.id);
        await updateDoc(orderRef, {
          status: 'confirmed',
          updatedAt: new Date()
        });
        
        console.log('✅ Order status changed back to confirmed:', order.id);
        fetchOrders(); // Refresh the orders list
      } catch (error) {
        console.error('❌ Error canceling trade:', error);
        alert('Failed to cancel trade. Please try again.');
      }
    }
  };

  // Check if order is within 2 hours of appointment time
  const isWithin2HoursOfAppointment = (order) => {
    const appointmentDateTime = getAppointmentDateTime(order);
    if (!appointmentDateTime) return false;
    
    const now = new Date();
    const timeDiff = appointmentDateTime.getTime() - now.getTime();
    
    return timeDiff <= 2 * 60 * 60 * 1000; // 2 hours in milliseconds
  };



  // Placeholder content for other sections
  const renderSection = () => {
    switch (selected) {
      case 'details':
        return (
          <div>
            <h2 style={{ color: '#f97316', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center' }}>Account Details</h2>
            <div style={{ fontSize: '1.1rem', color: '#1f2937', marginBottom: '2rem', textAlign: 'center' }}>
              <div><strong>Email:</strong> {user?.email}</div>
              <div style={{ marginTop: '1rem' }}>
                <strong>Display Name:</strong> {editing ? (
                  <form onSubmit={handleSave} style={{ display: 'inline-block', marginLeft: 8 }}>
                    <input
                      type="text"
                      value={displayName}
                      onChange={e => setDisplayName(e.target.value)}
                      style={{ padding: '0.3rem 0.7rem', borderRadius: 4, border: '1px solid #e5e7eb', marginRight: 8 }}
                      disabled={saving}
                    />
                    <button type="submit" style={{ background: '#f97316', color: '#fff', border: 'none', borderRadius: 4, padding: '0.3rem 0.9rem', fontWeight: 'bold', cursor: 'pointer', marginRight: 4 }} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" onClick={() => { setEditing(false); setDisplayName(user?.displayName || ""); }} style={{ background: '#eee', color: '#1f2937', border: 'none', borderRadius: 4, padding: '0.3rem 0.9rem', fontWeight: 'bold', cursor: 'pointer' }} disabled={saving}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <span style={{ marginLeft: 8 }}>{user?.displayName || <span style={{ color: '#aaa' }}>(not set)</span>} <button onClick={() => setEditing(true)} style={{ color: '#f97316', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.95rem' }}>Edit</button></span>
                )}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <strong>Area:</strong> {loadingProfile ? <span style={{ color: '#aaa' }}>Loading...</span> : (area || <span style={{ color: '#aaa' }}>(not set)</span>)}
              </div>
              {areaPrompt && (
                <form onSubmit={handleAreaSave} style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <input
                    type="text"
                    placeholder="Enter your Area"
                    value={areaEdit}
                    onChange={e => setAreaEdit(e.target.value)}
                    style={{ padding: '0.3rem 0.7rem', borderRadius: 4, border: '1px solid #e5e7eb', marginRight: 8 }}
                    required
                    disabled={areaSaving}
                  />
                  <button type="submit" style={{ background: '#f97316', color: '#fff', border: 'none', borderRadius: 4, padding: '0.3rem 0.9rem', fontWeight: 'bold', cursor: 'pointer' }} disabled={areaSaving}>
                    {areaSaving ? 'Saving...' : 'Save Area'}
                  </button>
                </form>
              )}
              <div style={{ marginTop: '0.5rem' }}>
                <strong>Member ID:</strong> {loadingProfile ? <span style={{ color: '#aaa' }}>Loading...</span> : (membershipNumber || <span style={{ color: '#aaa' }}>(not set)</span>)}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <strong>Contact Number:</strong> {loadingProfile ? (
                  <span style={{ color: '#aaa' }}>Loading...</span>
                ) : (contactEditing || contactPrompt) ? (
                  <form onSubmit={handleContactSave} style={{ display: 'inline-block', marginLeft: 8 }}>
                    <input
                      type="text"
                      placeholder="Enter your 7-digit phone number"
                      value={contactEdit}
                      onChange={e => setContactEdit(e.target.value.replace(/\D/g, '').substring(0, 7))}
                      style={{ padding: '0.3rem 0.7rem', borderRadius: 4, border: '1px solid #e5e7eb', marginRight: 8 }}
                      required
                      disabled={contactSaving}
                      maxLength={7}
                    />
                    <button type="submit" style={{ background: '#f97316', color: '#fff', border: 'none', borderRadius: 4, padding: '0.3rem 0.9rem', fontWeight: 'bold', cursor: 'pointer', marginRight: 4 }} disabled={contactSaving}>
                      {contactSaving ? 'Saving...' : 'Verify'}
                    </button>
                    <button type="button" onClick={() => { setContactEditing(false); setContactPrompt(false); setContactEdit(''); setError(''); }} style={{ background: '#eee', color: '#1f2937', border: 'none', borderRadius: 4, padding: '0.3rem 0.9rem', fontWeight: 'bold', cursor: 'pointer' }} disabled={contactSaving}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <span style={{ marginLeft: 8 }}>
                    {contact ? `+1868${contact}` : <span style={{ color: '#aaa' }}>(not set)</span>}
                    {phoneVerified && contact && <span style={{ color: 'green', marginLeft: 8, fontSize: '0.9rem' }}>✓ Verified</span>}
                    {!phoneVerified && contact && <span style={{ color: 'orange', marginLeft: 8, fontSize: '0.9rem' }}>⚠ Not verified</span>}
                    <button onClick={() => { setContactEditing(true); setContactEdit(contact || ''); setError(''); setMessage(''); }} style={{ color: '#f97316', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.95rem', marginLeft: 8 }}>
                      {contact ? 'Edit' : 'Add'}
                    </button>
                  </span>
                )}
              </div>
              
              {/* Show a verify button for unverified contacts */}
              {contact && !loadingProfile && !phoneVerified && !contactEditing && !contactPrompt && (
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <button 
                    onClick={() => sendPhoneVerificationCode()}
                    disabled={phoneVerificationLoading}
                    style={{ 
                      background: '#f97316', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: 4, 
                      padding: '0.5rem 1rem', 
                      fontWeight: 'bold', 
                      cursor: 'pointer',
                      opacity: phoneVerificationLoading ? 0.7 : 1
                    }}
                  >
                    {phoneVerificationLoading ? 'Sending...' : 'Verify Phone Number'}
                  </button>
                  {phoneVerificationError && (
                    <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                      {phoneVerificationError}
                    </div>
                  )}
                </div>
              )}
              
              {/* Phone verification modal */}
              {showPhoneVerification && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    background: 'white',
                    padding: '2rem',
                    borderRadius: 8,
                    maxWidth: '400px',
                    width: '90%',
                    textAlign: 'center'
                  }}>
                    <h3 style={{ color: '#f97316', marginBottom: '1rem' }}>Verify Phone Number</h3>
                    <p style={{ marginBottom: '1rem' }}>
                      Enter the 6-digit code sent to +1868{contact}
                    </p>
                    <input
                      type="text"
                      placeholder="Enter verification code"
                      value={phoneVerificationCode}
                      onChange={e => setPhoneVerificationCode(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: 4,
                        border: '1px solid #e5e7eb',
                        marginBottom: '1rem',
                        fontSize: '1rem',
                        textAlign: 'center'
                      }}
                      maxLength={6}
                    />
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                      <button
                        onClick={verifyPhoneCode}
                        disabled={phoneVerificationLoading || !phoneVerificationCode}
                        style={{
                          background: '#f97316',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '0.75rem 1.5rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          opacity: (phoneVerificationLoading || !phoneVerificationCode) ? 0.7 : 1
                        }}
                      >
                        {phoneVerificationLoading ? 'Verifying...' : 'Verify'}
                      </button>
                      <button
                        onClick={() => {
                          setShowPhoneVerification(false);
                          setPhoneVerificationCode('');
                          setPhoneVerificationError('');
                        }}
                        style={{
                          background: '#6b7280',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '0.75rem 1.5rem',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {phoneVerificationError && (
                      <div style={{ color: 'red', marginTop: '1rem', fontSize: '0.9rem' }}>
                        {phoneVerificationError}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {message && <div style={{ color: 'green', marginBottom: '1rem', textAlign: 'center' }}>{message}</div>}
            {error && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}
          </div>
        );
      case 'services':
        return (
          <div>
            <h2 style={{ color: '#f97316', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center' }}>Past Services</h2>
                            <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '2rem' }}>Your last 10 services</p>
            {loadingCompletedOrders ? (
              <div style={{ textAlign: 'center', color: '#1f2937' }}>
                <p>Loading completed services...</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', color: '#1f2937' }}>
                <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{error}</p>
                <button 
                  onClick={() => {
                    setError("");
                    // Trigger a re-fetch by changing the selected tab briefly
                    setSelected('details');
                    setTimeout(() => setSelected('services'), 100);
                  }}
                  style={{
                    background: '#f97316',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              </div>
            ) : completedOrders.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#1f2937' }}>
                <p>No completed services found.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {completedOrders.map((order) => {
                  // Safely handle missing or corrupted order data
                  if (!order || !order.id) {
                    return null; // Skip invalid orders
                  }
                  
                  return (
                    <div key={order.id} style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1rem',
                      background: '#f9fafb'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ color: '#1f2937', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                            {order.serviceName || 'Service'}
                          </h3>
                          <p style={{ color: '#6b7280', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                            {order.serviceDescription ? order.serviceDescription.split('\n').map((line, index) => (
                              <span key={index}>
                                {line}
                                {index < order.serviceDescription.split('\n').length - 1 && <br />}
                              </span>
                            )) : 'No description available'}
                          </p>
                          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            <div><strong>Order ID:</strong> {(() => {
                              console.log('UI RENDER - Order data:', { id: order.id, orderId: order.orderId, serviceName: order.serviceName });
                              return order.id || 'N/A';
                            })()}</div>
                            <div><strong>Price:</strong> ${(() => {
                              // Calculate correct price based on service name
                              if (order.serviceName) {
                                const washPrice = getWashPriceAmount(order.serviceName);
                                const appointmentPrice = order.appointmentType === 'free' ? 0 : 30;
                                return washPrice + appointmentPrice;
                              }
                              return order.price || 0;
                            })()}</div>
                            {order.location && (
                              <div><strong>Location:</strong> {order.location}</div>
                            )}
                            <div><strong>Completed:</strong> {
                              order.createdAt?.toDate?.() ? 
                                formatDate(order.createdAt.toDate()) : 
                                (order.createdAt ? formatDate(new Date(order.createdAt)) : 'N/A')
                            }</div>
                            {order.appointmentDate && order.appointmentTime && (
                              <>
                                <div><strong>Appointment:</strong> {formatDate(new Date(order.appointmentDate + 'T00:00:00'))} at {order.appointmentTime}</div>
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{ 
                          background: order.status === 'cancelled' ? '#ef4444' : '#10b981', 
                          color: '#fff', 
                          padding: '0.25rem 0.75rem', 
                          borderRadius: '12px', 
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          marginLeft: '1rem'
                        }}>
                          {order.status === 'cancelled' ? 'Cancelled' : 'Completed'}
                        </div>
                      </div>
                    </div>
                  );
                }).filter(Boolean)} {/* Remove any null entries */}
              </div>
            )}
          </div>
        );
      case 'orders':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: '#f97316', fontWeight: 'bold', textAlign: 'center', margin: 0 }}>Appointments</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>

                <button 
                  onClick={() => {
                    console.log('🔄 Manual refresh triggered');
                    fetchOrders();
                  }}
                  disabled={loadingOrders}
                  style={{
                    background: '#f97316',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.5rem 1rem',
                    fontWeight: 'bold',
                    cursor: loadingOrders ? 'not-allowed' : 'pointer',
                    opacity: loadingOrders ? 0.6 : 1,
                    fontSize: '0.9rem'
                  }}
                >
                  {loadingOrders ? 'Refreshing...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #f59e0b', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              <p style={{ 
                color: '#92400e', 
                margin: 0, 
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                ⏰ <strong>Give Up Limits:</strong> 1 order within 24 hours. Orders can only be given up more than 2 hours before appointment time.
              </p>
              <p style={{ 
                color: '#92400e', 
                margin: '0.75rem 0 0 0', 
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                📧 <strong>All orders are sent to your email inbox.</strong> Please check spam if not in your inbox. Please screenshot QR code just incase.
              </p>
            </div>
            
            {/* Trading Notice - Commented out for now
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #f59e0b', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <p style={{ 
                color: '#92400e', 
                margin: 0, 
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                ⏰ <strong>Trading Notice:</strong> Orders that are currently trading are removed from trading if not traded one hour before appointment time.
              </p>
            </div>
            */}

            {loadingOrders ? (
              <div style={{ textAlign: 'center', color: '#1f2937' }}>
                <p>Loading orders...</p>
              </div>
            ) : orders.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#1f2937' }}>
                <p>No open orders found.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {orders.map((order) => {
                  const now = new Date();
                  console.log('🔍 Processing order:', order.id, 'Type:', order.appointmentType, 'Status:', order.status, 'Current date:', now.toISOString().split('T')[0]);
                  
                  const isConfirmedFree = order.status === 'confirmed' && order.appointmentType === 'free';
                  const isConfirmedSpecial = order.status === 'confirmed' && order.appointmentType === 'special';
                  const createdAt = order.createdAt?.toDate?.() ? order.createdAt.toDate() : null;
                  
                  // Check if rebooking should be allowed for free appointments
                  let canRebookFree = true;
                  if (createdAt && order.appointmentDate && order.appointmentTime) {
                    const appointmentDateTime = getAppointmentDateTime(order);
                    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD format
                    const isSameDate = currentDate === order.appointmentDate;
                    const timeDiffToAppointment = appointmentDateTime.getTime() - now.getTime();
                    const within6HoursOfAppointment = timeDiffToAppointment <= 6 * 60 * 60 * 1000 && timeDiffToAppointment > 0;
                    const moreThan2HoursFromCreation = (now - createdAt) >= 2 * 60 * 60 * 1000;
                    
                    // Block rebooking if: more than 2 hours from creation OR (same date AND within 6 hours of appointment)
                    const shouldBlockRebook = moreThan2HoursFromCreation || (isSameDate && within6HoursOfAppointment);
                    canRebookFree = !shouldBlockRebook;
                  } else {
                    // Fallback to original 2-hour logic if no appointment date/time
                    canRebookFree = createdAt && (now - createdAt) < 2 * 60 * 60 * 1000;
                  }
                  
                  // Check if within 10 hours of appointment time for special appointments (same logic as free appointments)
                  let within10HoursOfAppointment = true;
                  if (createdAt && order.appointmentDate && order.appointmentTime) {
                    const appointmentDateTime = getAppointmentDateTime(order);
                    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD format
                    const isSameDate = currentDate === order.appointmentDate;
                    const timeDiffToAppointment = appointmentDateTime.getTime() - now.getTime();
                    const within6HoursOfAppointment = timeDiffToAppointment <= 6 * 60 * 60 * 1000 && timeDiffToAppointment > 0;
                    const moreThan2HoursFromCreation = (now - createdAt) >= 2 * 60 * 60 * 1000;
                    
                    // Block rebooking if: same date AND within 6 hours of appointment AND more than 2 hours from creation
                    const shouldBlockRebook = isSameDate && within6HoursOfAppointment && moreThan2HoursFromCreation;
                    within10HoursOfAppointment = !shouldBlockRebook;
                  } else {
                    // Fallback to original 10-hour logic if no appointment date/time or creation time
                  if (order.appointmentDate && order.appointmentTime) {
                    const appointmentDateTime = getAppointmentDateTime(order);
                    const timeDiff = appointmentDateTime.getTime() - now.getTime();
                    within10HoursOfAppointment = timeDiff > 10 * 60 * 60 * 1000; // More than 10 hours before appointment
                    }
                  }
                  
                  const alreadyRebooked = rebookedOrders.includes(order.id);
                  const hasChangedInDescription = order.description && order.description.toLowerCase().includes('changed');
                  const hasChangedInServiceDesc = order.serviceDescription && order.serviceDescription.toLowerCase().includes('changed');
                  const isChanged = hasChangedInDescription || hasChangedInServiceDesc;
                  
                  // Debug date comparison specifically
                  const debugCurrentDate = now.toISOString().split('T')[0];
                  const debugAppointmentDate = order.appointmentDate;
                  const debugIsSameDate = debugCurrentDate === debugAppointmentDate;
                  
                  console.log('🔍 Date comparison debug:', {
                    debugCurrentDate,
                    debugAppointmentDate,
                    debugIsSameDate,
                    currentDateType: typeof debugCurrentDate,
                    appointmentDateType: typeof debugAppointmentDate
                  });
                  
                  // Simple alert to see the values
                  console.log('CURRENT DATE:', debugCurrentDate);
                  console.log('APPOINTMENT DATE:', debugAppointmentDate);
                  console.log('SAME DATE?', debugIsSameDate);
                  console.log('TIME TO APPOINTMENT:', order.appointmentDate && order.appointmentTime ? 
                    ((getAppointmentDateTime(order).getTime() - now.getTime()) / (1000 * 60)).toFixed(0) + ' minutes' : 'N/A');
                  console.log('WITHIN 6 HOURS?', order.appointmentDate && order.appointmentTime ? 
                    ((getAppointmentDateTime(order).getTime() - now.getTime()) <= 6 * 60 * 60 * 1000 && 
                     (getAppointmentDateTime(order).getTime() - now.getTime()) > 0) : 'N/A');
                  console.log('MORE THAN 2 HOURS FROM CREATION?', createdAt ? ((now - createdAt) >= 2 * 60 * 60 * 1000) : 'N/A');
                  console.log('SHOULD BLOCK REBOOK?', order.appointmentDate && order.appointmentTime && createdAt ? 
                    (((now - createdAt) >= 2 * 60 * 60 * 1000) || 
                     (debugIsSameDate && 
                      ((getAppointmentDateTime(order).getTime() - now.getTime()) <= 6 * 60 * 60 * 1000 && 
                       (getAppointmentDateTime(order).getTime() - now.getTime()) > 0))) : 'N/A');
                  
                  console.log('🔍 Order rebook check:', {
                    orderId: order.id,
                    appointmentType: order.appointmentType,
                    status: order.status,
                    appointmentDate: order.appointmentDate,
                    appointmentTime: order.appointmentTime,
                    createdAt: createdAt ? createdAt.toISOString() : 'N/A',
                    currentDate: now.toISOString().split('T')[0],
                    isSameDate: order.appointmentDate ? (now.toISOString().split('T')[0] === order.appointmentDate) : 'N/A',
                    timeDiffToAppointment: order.appointmentDate && order.appointmentTime ? 
                      ((getAppointmentDateTime(order).getTime() - now.getTime()) / (1000 * 60 * 60)).toFixed(2) + ' hours' : 'N/A',
                    within6HoursOfAppointment: order.appointmentDate && order.appointmentTime ? 
                      ((getAppointmentDateTime(order).getTime() - now.getTime()) <= 6 * 60 * 60 * 1000 && 
                       (getAppointmentDateTime(order).getTime() - now.getTime()) > 0) : 'N/A',
                    moreThan2HoursFromCreation: createdAt ? ((now - createdAt) >= 2 * 60 * 60 * 1000) : 'N/A',
                    shouldBlockRebook: order.appointmentDate && order.appointmentTime && createdAt ? 
                      ((now.toISOString().split('T')[0] === order.appointmentDate) && 
                       ((getAppointmentDateTime(order).getTime() - now.getTime()) <= 6 * 60 * 60 * 1000 && 
                        (getAppointmentDateTime(order).getTime() - now.getTime()) > 0) && 
                       ((now - createdAt) >= 2 * 60 * 60 * 1000)) : 'N/A',
                    canRebookFree,
                    finalRebookDecision: order.appointmentType === 'free' ? 
                      (isConfirmedFree && !alreadyRebooked && !isChanged && canRebookFree) : 
                      (isConfirmedSpecial && !alreadyRebooked && !isChanged && within10HoursOfAppointment),
                    isConfirmedFree,
                    alreadyRebooked,
                    isChanged,
                    description: order.description,
                    serviceDescription: order.serviceDescription,
                    isChanged,
                    within10HoursOfAppointment,
                    descriptionLength: order.description?.length || 0,
                    serviceDescriptionLength: order.serviceDescription?.length || 0
                  });
                  return (
                    <div key={order.id} style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      background: '#f9fafb'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <div>
                          <h3 style={{ color: '#1f2937', marginBottom: '0.5rem', fontSize: '1.2rem' }}>
                            {order.serviceName || (order.appointmentType === 'free' ? 'Car Wash Service' : 'Service')}
                          </h3>
                          <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>
                            {order.serviceDescription ? order.serviceDescription.split('\n').map((line, index) => (
                              <span key={index}>
                                {line}
                                {index < order.serviceDescription.split('\n').length - 1 && <br />}
                              </span>
                            )) : getAppointmentTypeLabel(order.appointmentType)}
                            {isChanged && (
                              <span style={{ 
                                color: '#dc2626', 
                                fontWeight: 'bold', 
                                marginLeft: '0.5rem',
                                fontSize: '0.8rem'
                              }}>
                                (Rebooked)
                              </span>
                            )}
                          </p>
                          <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                            <div><strong>Order ID:</strong> {order.id}</div>
                            <div><strong>Price:</strong> ${(() => {
                              // Calculate correct price based on service name
                              if (order.serviceName) {
                                const washPrice = getWashPriceAmount(order.serviceName);
                                const appointmentPrice = order.appointmentType === 'free' ? 0 : 30;
                                return washPrice + appointmentPrice;
                              }
                              return order.price || 0;
                            })()}</div>
                            {order.location && (
                              <div><strong>Location:</strong> {order.location}</div>
                            )}
                            <div><strong>Status:</strong> <span style={{ 
                              color: order.status === 'confirmed' ? '#f97316' : '#10b981',
                              fontWeight: 'bold'
                            }}>
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span></div>
                            <div><strong>Booked on:</strong> {order.createdAt?.toDate?.() ? 
                              formatDate(order.createdAt.toDate()) : 
                              'N/A'
                            }</div>
                            {order.appointmentDate && order.appointmentTime && (
                              <>
                                <div><strong>Appointment:</strong> {formatDate(new Date(order.appointmentDate + 'T00:00:00'))} at {order.appointmentTime}</div>
                              </>
                            )}
                          </div>
                        </div>
                        {order.qrCodeDataURL ? (
                          <div style={{ 
                            textAlign: 'center', 
                            marginBottom: '1rem',
                            padding: '1rem',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            width: '100%'
                          }}>
                            <img 
                              src={order.qrCodeDataURL} 
                              alt="Order QR Code" 
                              style={{ 
                                width: '200px', 
                                height: '200px',
                                borderRadius: '8px',
                                backgroundColor: '#ffffff'
                              }} 
                            />
                            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.75rem', fontWeight: '500' }}>
                              📱 Scan QR code for order details
                            </p>
                            <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                              Screenshot this QR code in case of internet issues
                            </p>
                          </div>
                        ) : (
                          <div style={{ 
                            textAlign: 'center', 
                            marginBottom: '1rem',
                            padding: '1rem',
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            border: '1px solid #f59e0b',
                            width: '100%'
                          }}>
                            <p style={{ fontSize: '0.9rem', color: '#92400e', marginBottom: '0.5rem' }}>
                              QR code loading...
                            </p>
                            <button
                              onClick={() => {
                                // Force regenerate QR code for this order
                                const updatedOrders = orders.map(o => 
                                  o.id === order.id ? { ...o, qrCodeDataURL: null } : o
                                );
                                setOrders(updatedOrders);
                              }}
                              style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.8rem',
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Retry QR Code
                            </button>
                          </div>
                        )}
                        
                                                 {/* Info icon for Action Buttons - only show when there's at least one button */}
                        {/* Actions container */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', marginTop: '0.5rem', width: '100%' }}>
                        {(() => {
                          // Check if there will be any buttons shown for this order
                          const isConfirmedFree = order.status === 'confirmed' && order.appointmentType === 'free';
                          const isConfirmedSpecial = order.status === 'confirmed' && order.appointmentType === 'special';
                          const isTrading = order.status === 'trading';
                          
                          // Check conditions for Give up button (free appointments)
                          const canDelete = canDeleteOrder();
                          const within12Hours = isWithin12HoursOfAppointment(order);
                          const isTraded = (order.serviceName && order.serviceName.toLowerCase().includes('traded')) ||
                                          (order.serviceDescription && order.serviceDescription.toLowerCase().includes('traded'));
                          
                          const showGiveUpFree = isConfirmedFree && !isTraded && within12Hours;
                          const showGiveUpSpecial = isConfirmedSpecial && !isTraded && within12Hours;
                          // Transfer feature temporarily disabled
                          // const showTransfer = order.status === 'confirmed' && !isWithin2HoursOfAppointment(order) && 
                          //                    !(order.serviceName && order.serviceName.toLowerCase().includes('traded')) &&
                          //                    !(order.serviceDescription && order.serviceDescription.toLowerCase().includes('traded'));
                          const showTransfer = false;
                          const showCancelTrade = isTrading;
                          
                          // Only show info icon if at least one button will be displayed
                          return (showGiveUpFree || showGiveUpSpecial || showTransfer || showCancelTrade);
                        })() && (
                           <div style={{
                             position: 'relative',
                             display: 'flex',
                             marginBottom: '0.5rem'
                           }}>
                          <button
                                                               onClick={() => {
                                  const message = order.status === 'confirmed' 
                                    ? 'Give up: Free up timeslot for someone else (allowed more than 2 hours before appointment)'
                                    : 'Cancel Trade: Remove this order from the trading list and keep it for yourself';
                                  alert(message);
                                }}
                            style={{
                                 background: 'none',
                                 border: 'none',
                                 fontSize: '1.2rem',
                                 cursor: 'pointer',
                                 padding: '0.25rem',
                                 borderRadius: '50%',
                                 width: '28px',
                                 height: '28px',
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 color: '#0ea5e9',
                                 transition: 'background-color 0.2s'
                               }}
                               onMouseEnter={(e) => {
                                 e.target.style.backgroundColor = '#f0f9ff';
                               }}
                               onMouseLeave={(e) => {
                                 e.target.style.backgroundColor = 'transparent';
                               }}
                                                               title={
                                  order.status === 'confirmed' 
                                    ? 'Give up: Free up timeslot for someone else (allowed more than 2 hours before appointment)'
                                    : 'Cancel Trade: Remove this order from the trading list and keep it for yourself'
                                }
                             >
                               ℹ️
                             </button>
                           </div>
                         )}
                        
                        {/* Give up button for confirmed free appointments */}
                        {(() => {
                          const canDelete = canDeleteOrder();
                          const within12Hours = isWithin12HoursOfAppointment(order);
                          const isTraded = (order.serviceName && order.serviceName.toLowerCase().includes('traded')) ||
                                          (order.serviceDescription && order.serviceDescription.toLowerCase().includes('traded'));
                          
                          console.log('🔍 Give up button check for free appointment:', {
                            orderId: order.id,
                            isConfirmedFree,
                            canDelete,
                            within12Hours,
                            isTraded,
                            shouldShow: isConfirmedFree && !isTraded && within12Hours
                          });
                          
                          return isConfirmedFree && !isTraded && within12Hours;
                        })() && (
                          <button
                            disabled={!canDeleteOrder() || loadingOrders}
                            style={{
                              marginLeft: 0,
                              background: !canDeleteOrder() || loadingOrders ? '#e5e7eb' : '#ef4444',
                              color: !canDeleteOrder() || loadingOrders ? '#9ca3af' : '#fff',
                              border: 'none',
                              borderRadius: 6,
                              padding: '0.4rem 0.8rem',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              whiteSpace: 'nowrap',
                              cursor: !canDeleteOrder() || loadingOrders ? 'not-allowed' : 'pointer',
                              transition: 'background 0.2s, color 0.2s',
                            }}
                            onClick={() => handleGiveUpClick(order)}
                          >
                            {loadingOrders ? 'Deleting...' : 'Give up'}
                          </button>
                        )}
                        
                        {/* Give up button for confirmed special appointments */}
                                                 {(() => {
                           const canDelete = canDeleteOrder();
                           const within12Hours = isWithin12HoursOfAppointment(order);
                           const isTraded = (order.serviceName && order.serviceName.toLowerCase().includes('traded')) ||
                                           (order.serviceDescription && order.serviceDescription.toLowerCase().includes('traded'));
                           
                           return isConfirmedSpecial && !isTraded && within12Hours;
                         })() && (
                          <button
                            disabled={!canDeleteOrder() || loadingOrders}
                            style={{
                              marginLeft: 0,
                              background: !canDeleteOrder() || loadingOrders ? '#e5e7eb' : '#ef4444',
                              color: !canDeleteOrder() || loadingOrders ? '#9ca3af' : '#fff',
                              border: 'none',
                              borderRadius: 6,
                              padding: '0.4rem 0.8rem',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              whiteSpace: 'nowrap',
                              cursor: !canDeleteOrder() || loadingOrders ? 'not-allowed' : 'pointer',
                              transition: 'background 0.2s, color 0.2s',
                            }}
                            onClick={() => handleGiveUpClick(order)}
                          >
                            {loadingOrders ? 'Deleting...' : 'Give up'}
                          </button>
                        )}
                        
                        {/* Trade button temporarily disabled */}
                        {/* {order.status === 'confirmed' && !isWithin2HoursOfAppointment(order) && 
                         !(order.serviceName && order.serviceName.toLowerCase().includes('traded')) &&
                         !(order.serviceDescription && order.serviceDescription.toLowerCase().includes('traded')) && (
                          <button
                            style={{
                              marginLeft: 0,
                              background: '#3b82f6',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              padding: '0.5rem 1rem',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              transition: 'background 0.2s, color 0.2s',
                              whiteSpace: 'nowrap'
                            }}
                            onClick={() => handleTradeClick(order)}
                          >
                            Transfer
                          </button>
                        )} */}
                        
                        {/* Cancel Trade button for trading orders */}
                        {order.status === 'trading' && (
                          <button
                            style={{
                              marginLeft: 0,
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              padding: '0.5rem 1rem',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              transition: 'background 0.2s, color 0.2s',
                              whiteSpace: 'nowrap'
                            }}
                            onClick={() => handleCancelTradeClick(order)}
                          >
                            Cancel Trade
                          </button>
                        )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      case 'delete':
        return (
          <div>
            <h2 style={{ color: '#dc2626', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center' }}>Delete Account</h2>
            <div style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '8px', 
              padding: '1.5rem', 
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#dc2626', marginBottom: '1rem' }}>⚠️ Warning</h3>
              <p style={{ color: '#991b1b', marginBottom: '1rem', lineHeight: '1.6' }}>
                This action will permanently delete your account and all associated data including:
              </p>
              <ul style={{ 
                color: '#991b1b', 
                textAlign: 'left', 
                display: 'inline-block',
                marginBottom: '1rem'
              }}>
                <li>Your profile information</li>
                <li>All your orders and appointments</li>
                <li>Service history</li>
                <li>Account settings and preferences</li>
              </ul>
              <p style={{ color: '#991b1b', fontWeight: 'bold' }}>
                This action cannot be undone. Your account will be permanently deleted.
              </p>
            </div>
            
            {!showDeleteConfirm ? (
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.75rem 2rem',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  Delete My Account
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: '#dc2626', fontWeight: 'bold', marginBottom: '1rem' }}>
                  Are you absolutely sure you want to delete your account?
                </p>
                {user.providerData.some(provider => provider.providerId === 'google.com') ? (
                  <div>
                    <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                      You signed in with Google. Click the button below to confirm your identity.
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                        style={{
                          background: deletingAccount ? '#9ca3af' : '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.75rem 2rem',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          cursor: deletingAccount ? 'not-allowed' : 'pointer',
                          opacity: deletingAccount ? 0.6 : 1,
                          transition: 'background 0.2s'
                        }}
                      >
                        {deletingAccount ? 'Deleting...' : 'Confirm with Google'}
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setError("");
                        }}
                        disabled={deletingAccount}
                        style={{
                          background: '#6b7280',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.75rem 2rem',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          cursor: deletingAccount ? 'not-allowed' : 'pointer',
                          opacity: deletingAccount ? 0.6 : 1,
                          transition: 'background 0.2s'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                      Please enter your password to confirm this action.
                    </p>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <input
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{
                          padding: '0.75rem 1rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '1rem',
                          width: '100%',
                          maxWidth: '300px',
                          marginBottom: '1rem'
                        }}
                        disabled={deletingAccount}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount || !password}
                        style={{
                          background: (deletingAccount || !password) ? '#9ca3af' : '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.75rem 2rem',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          cursor: (deletingAccount || !password) ? 'not-allowed' : 'pointer',
                          opacity: (deletingAccount || !password) ? 0.6 : 1,
                          transition: 'background 0.2s'
                        }}
                      >
                        {deletingAccount ? 'Deleting...' : 'Yes, Delete Account'}
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setPassword("");
                          setError("");
                        }}
                        disabled={deletingAccount}
                        style={{
                          background: '#6b7280',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.75rem 2rem',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          cursor: deletingAccount ? 'not-allowed' : 'pointer',
                          opacity: deletingAccount ? 0.6 : 1,
                          transition: 'background 0.2s'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {error && (
              <div style={{ 
                color: '#dc2626', 
                marginTop: '1rem', 
                textAlign: 'center',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '1rem'
              }}>
                {error}
              </div>
            )}
          </div>
        );


      default:
        return null;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
      <DeviceRotationNotice />
      {/* Sidebar */}
      <div style={{
        minWidth: 220,
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
        padding: '2rem 1rem',
        marginRight: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        height: 'fit-content',
        gap: '0.5rem',
      }}>
        {NAV_ITEMS.map(item => (
          <button
            key={item.key}
            onClick={() => setSelected(item.key)}
            style={{
              background: selected === item.key ? '#f97316' : 'transparent',
              color: selected === item.key ? '#fff' : '#1f2937',
              border: 'none',
              borderRadius: 6,
              padding: '0.75rem 1rem',
              fontWeight: 'bold',
              fontSize: '1rem',
              cursor: 'pointer',
              marginBottom: 4,
              transition: 'background 0.2s, color 0.2s',
            }}
          >
            {item.label}
          </button>
        ))}
        <button
          onClick={onSignOut}
          style={{
            background: '#b91c1c',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '0.75rem 1rem',
            fontWeight: 'bold',
            fontSize: '1rem',
            cursor: 'pointer',
            marginTop: '2rem',
          }}
        >
          Sign Out
        </button>
      </div>
      {/* Main Content */}
      <div style={{ flex: 1, maxWidth: 600, background: '#fff', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', padding: '2.5rem 2rem', minWidth: 320 }}>
        {renderSection()}
      </div>

      {/* Rebook Modal */}
      <RebookModal
        isOpen={rebookModal.isOpen}
        onClose={handleRebookCancel}
        onConfirm={handleRebookConfirm}
        order={rebookModal.order}
      />
      
      {/* reCAPTCHA container for phone verification */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
    </div>
  );
}

export default Account; 